import { Component, DestroyRef, inject, OnInit, EventEmitter, Output } from '@angular/core';
import { SearchService } from '../../../common/services/search.service';
import { SiteService } from '../../../common/services/site.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PaginationMeta } from '../../../common/models/search.model';
import { APIDomainVersion, ContentType } from '../../../common/models/site.model';

@Component({
  selector: 'app-search-results',
  templateUrl: './search-results.component.html',
  styleUrl: './search-results.component.scss',
  standalone: false,
})
export class SearchResultsComponent implements OnInit {
  @Output() scrollToTop = new EventEmitter<void>();

  private destroyRef = inject(DestroyRef);
  searchQuery: string = '';
  version: string = '';
  results: Array<any> = [];
  currentDomainVersion: APIDomainVersion;
  loading: boolean = true;
  contentTypePriority: ContentType = 'api';
  pagination: PaginationMeta = {
    page: 1,
    pageSize: 10,
    pageCount: 0,
    total: 0,
  };

  // Page size options
  pageSizeOptions: number[] = [10, 25, 50, 100];
  selectedPageSize: number = 10;


  constructor(
    private searchService: SearchService,
    private siteService: SiteService
  ) {}

  ngOnInit(): void {
    this.siteService.currentDomainVersion$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((domainVersion: APIDomainVersion) => {
        this.currentDomainVersion = domainVersion;
      });

    this.searchService.searchQuery$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((query) => {
        this.searchQuery = query;
      });

    this.searchService.searchLoading$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((searchLoading) => {
        this.loading = searchLoading;
      });

    this.searchService.searchResults$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((searchResults) => {
        this.results = searchResults;
      });

    this.searchService.pagination$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((pagination) => {
        this.pagination = pagination;
        this.selectedPageSize = pagination.pageSize;
      });
  }

  handleSearchClose() {
    this.searchService.resetSearch();
  }

  goToPage(page: number) {
    if (page !== this.pagination.page) {
      this.searchService.goToPage(page);
      this.scrollToTop.emit();
    }
  }

  nextPage() {
    this.searchService.nextPage();
    this.scrollToTop.emit();
  }

  prevPage() {
    this.searchService.prevPage();
    this.scrollToTop.emit();
  }

  onPageSizeChange() {
    this.searchService.setPageSize(this.selectedPageSize);
  }

  // Helper to check if result is a docs page
  isDocsResult(result: any): boolean {
    return result.contentType === 'docs';
  }

  // Helper to check if result is an API result
  isApiResult(result: any): boolean {
    return result.contentType === 'api';
  }

  // Generate array of page numbers for display
  get visiblePages(): number[] {
    const { page, pageCount } = this.pagination;
    const delta = 2;
    const pages: number[] = [];

    const start = Math.max(1, page - delta);
    const end = Math.min(pageCount, page + delta);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }
}